<!-- src/components/settings/PublicationLogsTable.vue -->
<template>
  <div>
    <b-row class="mb-4 mt-5">
      <b-col cols="12" offset-sm="1" sm="10" class="d-flex align-items-center">
        <h3>
          {{ $t('settings.publicationLogs.title') }}
        </h3>
        <b-dropdown
            class="context-menu"
            toggle-class="dot-button"
            no-caret
            no-flip
            right
        >
          <template #button-content>
            <b-icon icon="three-dots-vertical" aria-hidden="true" scale="1"></b-icon>
          </template>
          <b-dropdown-item @click="downloadCsv">
            {{ $t('settings.analytics.downloadCSV') }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col cols="12" offset-sm="1" sm="10">
        <hr>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12" offset-sm="1" sm="10">
        <b-table
          :items="publicationLogs"
          :fields="fields"
          :busy="$apollo.loading"
          show-empty
          borderless
          dark
          fixed
        >
          <template #table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="{ width: field.width }"
            >
          </template>
          <template #head(published_at)>
            <div class="text-nowrap">
              {{ $t('settings.publicationLogs.columns.publishedAt') }}
            </div>
          </template>
          <template #head(page)>
            <div class="text-nowrap">
              {{ $t('settings.publicationLogs.columns.page') }}
            </div>
          </template>
          <template #head(user)>
            <div class="text-nowrap">
              {{ $t('settings.publicationLogs.columns.user') }}
            </div>
          </template>
          <template #head(project)>
            <div class="text-nowrap">
              {{ $t('settings.publicationLogs.columns.project') }}
            </div>
          </template>
          <template #empty>
            <span class="text-muted">{{ $t('settings.publicationLogs.empty') }}</span>
          </template>
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner small></b-spinner>
            </div>
          </template>
          <template #cell(published_at)="data">
            {{ formatDate(data.value) }}
          </template>
          <template #cell(page)="data">
            {{ data.item.page ? data.item.page.name : data.item.page_id }}
          </template>
          <template #cell(user)="data">
            {{ data.item.user ? (data.item.user.full_name || data.item.user.email) : data.item.user_id }}
          </template>
          <template #cell(project)="data">
            {{ data.item.project ? data.item.project.name : (data.item.project_id || '—') }}
          </template>
        </b-table>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import GetPublicationLogsByProjectId from '@/graphQlQueries/queries/getPublicationLogsByProjectId';
import GetPublicationLogsByFolderId from '@/graphQlQueries/queries/getPublicationLogsByFolderId';
import GetPublicationLogsByGroupId from '@/graphQlQueries/queries/getPublicationLogsByGroupId';

export default {
  name: 'PublicationLogsTable',
  props: {
    projectId: {
      type: Number,
      default: null,
    },
    groupId: {
      type: Number,
      default: null,
    },
    folderId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      publicationLogs: [],
    };
  },
  apollo: {
    publicationLogs: {
      query() {
        if (this.projectId) return GetPublicationLogsByProjectId;
        if (this.folderId) return GetPublicationLogsByFolderId;
        return GetPublicationLogsByGroupId;
      },
      variables() {
        if (this.projectId) return { projectId: this.projectId };
        if (this.folderId) return { folderId: this.folderId };
        return { groupId: this.groupId };
      },
      skip() {
        return !this.projectId && !this.folderId && !this.groupId;
      },
      update(data) {
        const logs = data.publication_logs || [];
        // Re-sort by published_at desc (distinct_on forces order_by to start with project_id)
        if (this.folderId) {
          return [...logs].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        }
        return logs;
      },
    },
  },
  computed: {
    fields() {
      const cols = [
        {
          key: 'published_at',
          label: this.$t('settings.publicationLogs.columns.publishedAt'),
          sortable: true,
          width: '160px',
        },
        {
          key: 'page',
          label: this.$t('settings.publicationLogs.columns.page'),
          sortable: true,
          sortByFormatted: true,
          formatter: (_value, _key, item) => (item.page ? item.page.name : (item.page_id || '')),
          width: 'auto',
        },
        {
          key: 'user',
          label: this.$t('settings.publicationLogs.columns.user'),
          width: '180px',
        },
      ];
      if (!this.projectId) {
        cols.push({
          key: 'project',
          label: this.$t('settings.publicationLogs.columns.project'),
          sortable: true,
          sortByFormatted: true,
          formatter: (_value, _key, item) => (item.project ? item.project.name : (item.project_id || '')),
          width: '160px',
        });
      }
      return cols;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString();
    },
    downloadCsv() {
      const headers = this.fields.map((f) => f.label);
      const rows = this.publicationLogs.map((item) => {
        const cols = [
          this.formatDate(item.published_at),
          item.page ? item.page.name : (item.page_id || ''),
          item.user ? (item.user.full_name || item.user.email) : (item.user_id || ''),
        ];
        if (!this.projectId) {
          cols.push(item.project ? item.project.name : (item.project_id || ''));
        }
        return cols.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
      });
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'publication-logs.csv';
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .btn.dot-button {
  color: #b9b9b9;
  background: transparent;
  border: none;
  margin-bottom: 0.5rem;

  &:hover {
    color: #fff;
  }
}

.table-dark {
  background-color: transparent;
}

::v-deep table {
  font-size: 15px;
  border-collapse: separate;
  border-spacing: 0 8px;

  thead th {
    text-transform: uppercase;
    font-size: 13px;
    border-color: transparent;
  }

  tbody tr {
    background-color: #53575B;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);

    > td {
      padding: 7px 8px;
    }
  }
}
</style>
